import { Badge, Stack, Text, Tooltip } from "@mantine/core";

/**
 * How far a verifier model judged a run to have got, and how sure it was.
 *
 * TWO numbers, and the badge deliberately shows the first while letting the second change how the
 * first is drawn. A run at 50% with a peaked distribution is confidently half-finished; the same
 * 50% with a flat one means the verifier could not tell. Printing only the grade collapses those
 * into one number and answers neither question — so a low-certainty reading is drawn recessively
 * (outline, grey) rather than in the confident colour its score would otherwise earn.
 *
 * Rendered only for runs that opted into verification, which is a small minority. An absent score
 * is NOT a zero: zero is the verifier's real verdict that the run achieved nothing.
 */

/** Below this the verifier was not committing, and the badge should not look like it was. */
const UNCERTAIN_BELOW = 0.4;

function band(score) {
  if (score >= 0.8) return { color: "teal", label: "on track" };
  if (score >= 0.4) return { color: "yellow", label: "partial" };
  return { color: "red", label: "little progress" };
}

function ProgressBadge({
  score,
  certainty,
  lowest,
  readings,
  verifier,
  size = "sm",
}) {
  if (score === null || score === undefined) return null;

  const { color, label } = band(score);
  const unsure = certainty !== null && certainty !== undefined && certainty < UNCERTAIN_BELOW;

  return (
    <Tooltip
      multiline
      w={280}
      label={
        <Stack gap={2}>
          <Text size="xs" fw={600}>
            Verified progress: {Math.round(score * 100)}% — {label}
          </Text>
          {certainty !== null && certainty !== undefined && (
            <Text size="xs">
              Verifier certainty: {Math.round(certainty * 100)}%
              {unsure && " — it was not committing to this grade"}
            </Text>
          )}
          {lowest !== null && lowest !== undefined && lowest < score && (
            <Text size="xs">Lowest during the run: {Math.round(lowest * 100)}%</Text>
          )}
          {readings > 1 && <Text size="xs">{readings} readings</Text>}
          {verifier && (
            <Text size="xs" c="dimmed">
              Graded by {verifier}. Scores from different verifiers are not comparable.
            </Text>
          )}
        </Stack>
      }
    >
      <Badge
        size={size}
        color={unsure ? "gray" : color}
        variant={unsure ? "outline" : "light"}
      >
        {Math.round(score * 100)}%
      </Badge>
    </Tooltip>
  );
}

export default ProgressBadge;
